import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useSession } from '@/lib/auth';
import { safeGetLocalStorage, safeRemoveLocalStorage } from '@/lib/utils/storageUtils';

const FORM_STORAGE_KEY = 'atrivial_pending_trivia_form';
const FORM_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Hook to detect pending trivia form data and redirect user to complete registration
 *
 * Use this in App.tsx or layout component to handle cases where OAuth redirects
 * to home page instead of back to /get-trivia
 */
export const usePendingFormRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useSession();

  useEffect(() => {
    // Only check if we're not already on the get-trivia page
    if (location.pathname === '/get-trivia') {
      return;
    }

    try {
      const savedData = safeGetLocalStorage(FORM_STORAGE_KEY);

      if (savedData && isAuthenticated && user) {
        const parsed = JSON.parse(savedData);
        const age = Date.now() - parsed.timestamp;

        // If data exists and hasn't expired, redirect to get-trivia page
        if (age < FORM_EXPIRY_MS) {
          console.log('Pending form data detected, redirecting to /get-trivia');
          navigate('/get-trivia', { replace: true });
        } else {
          // Clean up expired data
          safeRemoveLocalStorage(FORM_STORAGE_KEY);
        }
      }
    } catch (err) {
      console.error('Error checking pending form data:', err);
      // Clean up corrupted data
      safeRemoveLocalStorage(FORM_STORAGE_KEY);
    }
  }, [isAuthenticated, user, location.pathname, navigate]);
};
